import { lazy, ComponentType } from 'react';
import Hero from '@/components/sections/Hero';

// Hero is eagerly loaded — it is the LCP element and must render immediately
// All other sections are lazy-loaded for code splitting
const WorkSnapshots = lazy(() => import('@/components/sections/WorkSnapshots'));
const TeamMarquee = lazy(() => import('@/components/sections/TeamMarquee'));
const VixieShowcase = lazy(() => import('@/components/sections/VixieShowcase'));
const FeaturedEvents = lazy(() => import('@/components/sections/FeaturedEvents'));
const TrustLogos = lazy(() => import('@/components/sections/TrustLogos'));
const GlobalMap = lazy(() => import('@/components/sections/GlobalMap'));
const ConversionClose = lazy(() => import('@/components/sections/ConversionClose'));
const BlogSection = lazy(() => import('@/components/sections/BlogSection'));
const FAQSection = lazy(() => import('@/components/sections/FAQSection'));
const LinkedInFeed = lazy(() => import('@/components/sections/LinkedInFeed'));
const Testimonials = lazy(() => import('@/components/sections/Testimonials'));

// Company sections
const CompanyHero = lazy(() => import('@/components/sections/CompanyHero'));
const CompanyPhotoGrid = lazy(() => import('@/components/sections/CompanyPhotoGrid'));
const CompanyStats = lazy(() => import('@/components/sections/CompanyStats'));
const CompanyValues = lazy(() => import('@/components/sections/CompanyValues'));
const CompanyLeadership = lazy(() => import('@/components/sections/CompanyLeadership'));
const CompanyPresence = lazy(() => import('@/components/sections/CompanyPresence'));
const CompanyCTA = lazy(() => import('@/components/sections/CompanyCTA'));
const CompanyAwards = lazy(() => import('@/components/sections/CompanyAwards'));

// Solutions sections
const SolutionsHero = lazy(() => import('@/components/sections/SolutionsHero'));
const SolutionsOfferings = lazy(() => import('@/components/sections/SolutionsOfferings'));
const SolutionsWhy = lazy(() => import('@/components/sections/SolutionsWhy'));
const SolutionsCTA = lazy(() => import('@/components/sections/SolutionsCTA'));

// Work sections
const WorkHero = lazy(() => import('@/components/sections/WorkHero'));
const WorkFeatured = lazy(() => import('@/components/sections/WorkFeatured'));
const WorkGrid = lazy(() => import('@/components/sections/WorkGrid'));

// Shared
const PatentDisclaimer = lazy(() => import('@/components/sections/PatentDisclaimer'));

// Vixi sections
const VixiHeroSection = lazy(() => import('@/components/vixi-sections/VixiHeroSection'));
const VixiStatsSection = lazy(() => import('@/components/vixi-sections/VixiStatsSection'));
const VixiModulesSection = lazy(() => import('@/components/vixi-sections/VixiModulesSection'));
const VixiVixieShowcaseWrapper = lazy(() => import('@/components/vixi-sections/VixiVixieShowcaseWrapper'));
const VixiFanEngagementWrapper = lazy(() => import('@/components/vixi-sections/VixiFanEngagementWrapper'));
const VixiLightShowHighlight = lazy(() => import('@/components/vixi-sections/VixiLightShowHighlight'));
const VixiTestimonialSection = lazy(() => import('@/components/vixi-sections/VixiTestimonialSection'));
const VixiCTASection = lazy(() => import('@/components/vixi-sections/VixiCTASection'));

// Maps CMS section_type strings to React components
export const SECTION_COMPONENTS: Record<string, ComponentType> = {
  hero: Hero,
  work_snapshots: WorkSnapshots,
  team_marquee: TeamMarquee,
  vixie_showcase: VixieShowcase,
  featured_events: FeaturedEvents,
  trust_logos: TrustLogos,
  global_map: GlobalMap,
  conversion_close: ConversionClose,
  blog: BlogSection,
  faq: FAQSection,
  linkedin_feed: LinkedInFeed,
  testimonials: Testimonials,
  // Company
  company_hero: CompanyHero,
  company_photo_grid: CompanyPhotoGrid,
  company_stats: CompanyStats,
  company_values: CompanyValues,
  company_leadership: CompanyLeadership,
  company_presence: CompanyPresence,
  company_cta: CompanyCTA,
  company_awards: CompanyAwards,
  // Solutions
  solutions_hero: SolutionsHero,
  solutions_offerings: SolutionsOfferings,
  solutions_why: SolutionsWhy,
  solutions_cta: SolutionsCTA,
  // Work
  work_hero: WorkHero,
  work_featured: WorkFeatured,
  work_grid: WorkGrid,
  // Shared
  patent_disclaimer: PatentDisclaimer,
  // Vixi
  vixi_hero: VixiHeroSection,
  vixi_stats: VixiStatsSection,
  vixi_modules: VixiModulesSection,
  vixi_vixie_showcase: VixiVixieShowcaseWrapper,
  vixi_fan_engagement_stats: VixiFanEngagementWrapper,
  vixi_lightshow_highlight: VixiLightShowHighlight,
  vixi_testimonial: VixiTestimonialSection,
  vixi_cta: VixiCTASection,
  vixi_team_marquee: TeamMarquee,
};

// Human-readable labels for each section type
export const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero',
  work_snapshots: 'What We Do',
  team_marquee: 'Team Marquee',
  vixie_showcase: 'Vixie Showcase',
  featured_events: 'Featured Events',
  trust_logos: 'Trust Logos / Partners',
  global_map: 'Global Map',
  conversion_close: 'CTA / Conversion Close',
  blog: 'Blog Feed',
  faq: 'FAQ',
  linkedin_feed: 'LinkedIn Feed',
  testimonials: 'Testimonials',
  // Company
  company_hero: 'Company Hero',
  company_photo_grid: 'Company Photo Grid',
  company_stats: 'Company Stats',
  company_values: 'Company Values',
  company_leadership: 'Company Leadership',
  company_presence: 'Company Global Presence',
  company_cta: 'Company CTA',
  company_awards: 'Company Awards',
  // Solutions
  solutions_hero: 'Solutions Hero',
  solutions_offerings: 'Solutions Offerings',
  solutions_why: 'Why Famous',
  solutions_cta: 'Solutions CTA',
  // Work
  work_hero: 'Work Hero',
  work_featured: 'Work Featured Projects',
  work_grid: 'Work Project Grid',
  patent_disclaimer: 'Patent Disclaimer',
  // Vixi
  vixi_hero: 'Vixi Hero',
  vixi_stats: 'Vixi Stats',
  vixi_modules: 'Vixi Modules',
  vixi_vixie_showcase: 'Vixi Vixie Showcase',
  vixi_fan_engagement_stats: 'Vixi Fan Engagement Stats',
  vixi_lightshow_highlight: 'Vixi LightShow Highlight',
  vixi_testimonial: 'Vixi Testimonial',
  vixi_cta: 'Vixi CTA',
  vixi_team_marquee: 'Vixi Team Marquee',
};
